/**
 * ServiceClosurePanel — stage-agnostic closure surface on the SR detail page.
 *
 * Rendering is driven entirely by the backend's `service_closure` object:
 *
 *   status null / withdrawn      → slim "Request service closure" row (if allowed)
 *   pending_admin_action         → full "under review" state screen: headline,
 *                                  progress timeline, reason/note, and a side
 *                                  panel with the two choices the customer has
 *   declined_by_admin            → "your service continues" card with the
 *                                  admin's reason (+ re-request if allowed)
 *   approved_by_admin            → "closed" card with what happened next
 *   no closure row but CANCELLED → closed card (platform wording from the server)
 *
 * Button visibility comes from `can_request_closure` / `can_withdraw_request`
 * / `can_remind_admin` only — no eligibility is re-derived here.
 */
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { formatDate } from '@finmin/utils';
import {
  ArrowRight,
  Bell,
  CheckCircle2,
  CircleSlash,
  FileLock2,
  Loader2,
  PauseCircle,
  ShieldCheck,
  Wallet,
  XCircle,
} from 'lucide-react';
import { Button, toast } from '@finmin/ui';
import type { CustomerServiceRequestResponse } from '@/api/service-requests';
import {
  closureErrorMessage,
  useRemindAdminAboutClosure,
  useWithdrawClosureRequest,
} from '@/api/service-closure';
import { PALETTE_AMBER, PALETTE_FOREST } from '../tokens';
import { RequestClosureDialog } from './request-closure-dialog';

interface Props {
  request: CustomerServiceRequestResponse;
  serviceTitle: string;
  /** Applied to the wrapper only when something renders (no empty spacing). */
  className?: string;
}

export function ServiceClosurePanel({ request, serviceTitle, className }: Props) {
  const body = useClosurePanelBody(request, serviceTitle);
  if (!body) return null;
  return <div className={className}>{body}</div>;
}

function useClosurePanelBody(request: CustomerServiceRequestResponse, serviceTitle: string): React.ReactNode {
  const [open, setOpen] = React.useState(false);
  const closure = request.service_closure;
  if (!closure) return null;

  const status = closure.status;
  const requestButton = closure.can_request_closure ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => setOpen(true)}
      className="w-full sm:w-auto"
    >
      <CircleSlash className="mr-1.5 h-3.5 w-3.5" aria-hidden />
      Request service closure
    </Button>
  ) : null;

  const dialog = (
    <RequestClosureDialog
      serviceRequestId={request.id}
      serviceTitle={serviceTitle}
      open={open}
      onOpenChange={setOpen}
    />
  );

  // -- Pending review ---------------------------------------------------------
  if (status === 'pending_admin_action' && closure.request_id) {
    return (
      <PendingScreen
        serviceRequestId={request.id}
        closureRequestId={closure.request_id}
        serviceTitle={serviceTitle}
        reasonLabel={closure.reason_label}
        description={closure.description}
        requestedAt={closure.requested_at}
        reviewDueAt={closure.review_due_at}
        canWithdraw={closure.can_withdraw_request}
        canRemind={closure.can_remind_admin}
      />
    );
  }

  // -- Closed (approved, or cancelled without a closure row) -----------------
  if (status === 'approved_by_admin' || (status == null && closure.service_state === 'CANCELLED')) {
    return (
      <ClosedScreen
        serviceTitle={serviceTitle}
        byCustomer={closure.origin === 'customer'}
        decidedAt={closure.decided_at}
        reasonLabel={closure.reason_label}
      />
    );
  }

  // -- Declined ---------------------------------------------------------------
  if (status === 'declined_by_admin') {
    return (
      <>
        <DeclinedScreen
          serviceTitle={serviceTitle}
          decidedAt={closure.decided_at}
          declineReason={closure.decline_reason}
          action={requestButton}
        />
        {dialog}
      </>
    );
  }

  // -- Normal service view (no request, or withdrawn) -------------------------
  if (!requestButton) return null;
  return (
    <>
      <div className="flex flex-col gap-3 rounded-2xl border border-dashed border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:py-2.5">
        <p className="text-xs text-muted-foreground">
          Changed your mind? You can ask us to close this service before payment is completed.
        </p>
        {requestButton}
      </div>
      {dialog}
    </>
  );
}

// ── Under review ─────────────────────────────────────────────────────────────

interface PendingProps {
  serviceRequestId: string;
  closureRequestId: string;
  serviceTitle: string;
  reasonLabel: string | null;
  description: string | null;
  requestedAt: string | null;
  reviewDueAt: string | null;
  canWithdraw: boolean;
  canRemind: boolean;
}

function PendingScreen({
  serviceRequestId,
  closureRequestId,
  serviceTitle,
  reasonLabel,
  description,
  requestedAt,
  reviewDueAt,
  canWithdraw,
  canRemind,
}: PendingProps) {
  const vars = { serviceRequestId, closureRequestId };

  const withdraw = useWithdrawClosureRequest({
    onSuccess: () => toast.success('Closure request withdrawn', 'Your service has resumed.'),
    onError: (err) =>
      toast.error(closureErrorMessage(err, 'Could not withdraw the request. Please try again.')),
  });
  const remind = useRemindAdminAboutClosure({
    onSuccess: () => toast.success('Reminder sent', 'Our team has been notified about your request.'),
    onError: (err) =>
      toast.error(closureErrorMessage(err, 'Could not send a reminder. Please try again.')),
  });
  const busy = withdraw.isPending || remind.isPending;

  const dueLong = reviewDueAt ? formatDate(reviewDueAt, 'EEEE, dd MMM') : null;

  return (
    <StateCard accent={PALETTE_AMBER}>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-8">
        {/* ── Main column ─────────────────────────────────────────────────── */}
        <div className="min-w-0">
          <StateHeader
            accent={PALETTE_AMBER}
            icon={PauseCircle}
            eyebrow="Closure request · Under review"
            title="Your request is with our team"
            lede={
              dueLong
                ? `We'll reply by ${dueLong}. Nothing is needed from you until then — your ${serviceTitle} is paused, not lost.`
                : `We'll reply within five working days. Nothing is needed from you until then — your ${serviceTitle} is paused, not lost.`
            }
          />

          <Timeline
            className="mt-7 sm:pl-16"
            steps={[
              {
                state: 'done',
                label: 'Request sent',
                detail: requestedAt ? formatDate(requestedAt, 'dd MMM, HH:mm') : 'Received',
              },
              {
                state: 'active',
                label: 'Team review',
                detail: reviewDueAt ? `Reply by ${formatDate(reviewDueAt, 'dd MMM')}` : 'Within 5 working days',
              },
              {
                state: 'todo',
                label: 'Decision',
                detail: 'Closed, or resumes where it left off',
              },
            ]}
          />

          {(reasonLabel || description) && (
            <dl className="mt-7 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[auto_minmax(0,1fr)] sm:pl-16">
              {reasonLabel && (
                <>
                  <dt className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground sm:pt-0.5">
                    Reason
                  </dt>
                  <dd className="font-medium text-foreground">{reasonLabel}</dd>
                </>
              )}
              {description && (
                <>
                  <dt className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground sm:pt-0.5">
                    Your note
                  </dt>
                  <dd className="text-muted-foreground">“{description}”</dd>
                </>
              )}
            </dl>
          )}

          <div
            className="mt-7 rounded-2xl px-4 py-3.5 sm:ml-16"
            style={{ backgroundColor: `${PALETTE_FOREST}0A` }}
          >
            <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: PALETTE_FOREST }}>
              While it's paused
            </p>
            <ul className="mt-2 grid gap-2 text-sm text-foreground sm:grid-cols-3 sm:gap-4">
              <Fact icon={Wallet}>No payment will be taken.</Fact>
              <Fact icon={FileLock2}>Uploads and workflow steps are locked.</Fact>
              <Fact icon={ShieldCheck}>Everything you've shared stays safely stored.</Fact>
            </ul>
          </div>
        </div>

        {/* ── Side column: the two choices ─────────────────────────────────── */}
        <aside className="flex flex-col gap-4">
          {(canRemind || canWithdraw) && (
            <div className="rounded-2xl border border-border bg-background p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                What you can do
              </p>
              <div className="mt-3 space-y-4">
                {canWithdraw && (
                  <Choice
                    title="Changed your mind?"
                    hint="Withdraws the request. Work resumes right away, from where it stopped."
                  >
                    <Button
                      type="button"
                      size="sm"
                      disabled={busy}
                      onClick={() => withdraw.mutate(vars)}
                      className="w-full"
                    >
                      {withdraw.isPending ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                      ) : (
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      )}
                      Keep this service
                    </Button>
                  </Choice>
                )}
                {canRemind && (
                  <Choice
                    title="Waiting too long?"
                    hint="Sends a nudge to the team reviewing your request."
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => remind.mutate(vars)}
                      className="w-full"
                    >
                      {remind.isPending ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                      ) : (
                        <Bell className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      )}
                      Remind the team
                    </Button>
                  </Choice>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>
    </StateCard>
  );
}

// ── Closed ───────────────────────────────────────────────────────────────────

function ClosedScreen({
  serviceTitle,
  byCustomer,
  decidedAt,
  reasonLabel,
}: {
  serviceTitle: string;
  byCustomer: boolean;
  decidedAt: string | null;
  reasonLabel: string | null;
}) {
  const when = decidedAt ? ` on ${formatDate(decidedAt, 'dd MMM yyyy')}` : '';
  return (
    <StateCard accent={PALETTE_FOREST}>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-8">
        <div className="min-w-0">
          <StateHeader
            accent={PALETTE_FOREST}
            icon={CheckCircle2}
            eyebrow={`Closed${when}`}
            title={`Your ${serviceTitle} is closed`}
            lede={
              byCustomer
                ? `You asked us to close this service${reasonLabel ? ` (${reasonLabel.toLowerCase()})` : ''} and our team has approved it. Nothing more is needed from you.`
                : 'Our team closed this service on the FinminOS platform. Nothing more is needed from you — we will reach out if anything else is required.'
            }
          />
          <ul className="mt-6 grid gap-2 text-sm text-foreground sm:grid-cols-2 sm:pl-16">
            <Fact icon={Wallet}>Any pending payment was voided.</Fact>
            <Fact icon={CheckCircle2}>Redeemed reward points were returned.</Fact>
            <Fact icon={ShieldCheck}>Your documents are kept for your records.</Fact>
            <Fact icon={FileLock2}>This request is now read-only.</Fact>
          </ul>
        </div>

        <aside className="rounded-2xl border border-border bg-background p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Next
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Need something else? Start a new service any time — it takes a minute.
          </p>
          <Link
            to="/dashboard"
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-all hover:shadow-md active:scale-[0.98]"
            style={{ backgroundColor: PALETTE_FOREST }}
          >
            Browse services
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </aside>
      </div>
    </StateCard>
  );
}

// ── Declined ─────────────────────────────────────────────────────────────────

function DeclinedScreen({
  serviceTitle,
  decidedAt,
  declineReason,
  action,
}: {
  serviceTitle: string;
  decidedAt: string | null;
  declineReason: string | null;
  action: React.ReactNode;
}) {
  const when = decidedAt ? ` · ${formatDate(decidedAt, 'dd MMM yyyy')}` : '';
  return (
    <StateCard accent="hsl(var(--muted-foreground))">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-8">
        <div className="min-w-0">
          <StateHeader
            accent="hsl(var(--muted-foreground))"
            icon={XCircle}
            eyebrow={`Closure request declined${when}`}
            title={`Your ${serviceTitle} continues`}
            lede="Our team reviewed your request and decided to keep the service running. Everything picks up exactly where it left off."
          />
          {declineReason && (
            <blockquote
              className="mt-6 rounded-2xl border-l-4 bg-muted px-4 py-3 text-sm text-foreground sm:ml-16"
              style={{ borderColor: PALETTE_AMBER }}
            >
              <span className="block text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                Message from our team
              </span>
              <span className="mt-1 block leading-relaxed">{declineReason}</span>
            </blockquote>
          )}
        </div>

        <aside className="rounded-2xl border border-border bg-background p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Still want to close it?
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            {action
              ? 'You can send one more request. Reply in chat first if you want to discuss the decision.'
              : 'Another closure request is not available for this service. Reply in chat if you want to discuss the decision.'}
          </p>
          {action && <div className="mt-4">{action}</div>}
        </aside>
      </div>
    </StateCard>
  );
}

// ── Building blocks ──────────────────────────────────────────────────────────

/** White card on the cream page, with a coloured top edge that carries the state. */
function StateCard({ accent, children }: { accent: string; children: React.ReactNode }) {
  return (
    <section
      aria-live="polite"
      className="animate-fade-in overflow-hidden rounded-3xl border border-border bg-background shadow-sm"
      style={{ borderTop: `4px solid ${accent}` }}
    >
      <div className="p-5 sm:p-7">{children}</div>
    </section>
  );
}

function StateHeader({
  accent,
  icon: Icon,
  eyebrow,
  title,
  lede,
}: {
  accent: string;
  icon: React.ElementType;
  eyebrow: string;
  title: string;
  lede: string;
}) {
  return (
    <div className="flex items-start gap-4">
      <div
        className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2"
        style={{ backgroundColor: `${accent}12`, borderColor: `${accent}30` }}
      >
        <Icon className="h-6 w-6" style={{ color: accent }} aria-hidden />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: accent }}>
          {eyebrow}
        </p>
        <h2 className="font-display mt-1 text-2xl font-bold leading-tight text-foreground sm:text-3xl">
          {title}
        </h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">{lede}</p>
      </div>
    </div>
  );
}

type StepState = 'done' | 'active' | 'todo';
interface Step {
  state: StepState;
  label: string;
  detail: string;
}

/** Horizontal 3-step progress on sm+, vertical rail on phones. */
function Timeline({ steps, className }: { steps: Step[]; className?: string }) {
  const color = (s: StepState) =>
    s === 'done' ? PALETTE_FOREST : s === 'active' ? PALETTE_AMBER : 'hsl(var(--border))';

  return (
    <div className={className} aria-label="Progress">
      {/* sm+ : horizontal */}
      <ol className="hidden sm:grid sm:grid-cols-3">
        {steps.map((step, i) => {
          // A connector is "filled" only once the step before it is done.
          const lineBefore = i === 0 ? null : color(steps[i - 1]!.state === 'done' ? 'done' : 'todo');
          const lineAfter = i === steps.length - 1 ? null : color(step.state === 'done' ? 'done' : 'todo');
          return (
            <li key={step.label} className="relative flex flex-col items-center px-2 text-center">
              <div className="relative flex h-5 w-full items-center justify-center">
                {lineBefore && (
                  <span className="absolute left-0 right-1/2 top-1/2 h-0.5 -translate-y-1/2" style={{ backgroundColor: lineBefore }} aria-hidden />
                )}
                {lineAfter && (
                  <span className="absolute left-1/2 right-0 top-1/2 h-0.5 -translate-y-1/2" style={{ backgroundColor: lineAfter }} aria-hidden />
                )}
                <StepDot state={step.state} />
              </div>
              <p className="mt-2 text-sm font-semibold text-foreground">{step.label}</p>
              <p className="text-xs text-muted-foreground">{step.detail}</p>
            </li>
          );
        })}
      </ol>

      {/* phones : vertical */}
      <ol className="space-y-0 sm:hidden">
        {steps.map((step, i) => (
          <li key={step.label} className="relative flex gap-3 pb-5 last:pb-0">
            {i < steps.length - 1 && (
              <span
                className="absolute left-[9px] top-5 h-full w-0.5"
                style={{ backgroundColor: color(step.state === 'done' ? 'done' : 'todo') }}
                aria-hidden
              />
            )}
            <div className="relative flex h-5 w-5 shrink-0 items-center justify-center">
              <StepDot state={step.state} />
            </div>
            <div>
              <p className="text-sm font-semibold leading-5 text-foreground">{step.label}</p>
              <p className="text-xs text-muted-foreground">{step.detail}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function StepDot({ state }: { state: StepState }) {
  if (state === 'active') {
    return (
      <span className="relative flex h-5 w-5 items-center justify-center">
        {/* Static halo (no animation): a pulsing ring here made some screen
            captures render the whole card washed out. */}
        <span className="absolute inline-flex h-full w-full rounded-full" style={{ backgroundColor: `${PALETTE_AMBER}33` }} aria-hidden />
        <span className="relative inline-flex h-3.5 w-3.5 rounded-full border-2 border-background" style={{ backgroundColor: PALETTE_AMBER }} aria-hidden />
      </span>
    );
  }
  if (state === 'done') {
    return (
      <span className="relative z-10 flex h-5 w-5 items-center justify-center rounded-full" style={{ backgroundColor: PALETTE_FOREST }}>
        <CheckCircle2 className="h-3.5 w-3.5 text-white" aria-hidden />
      </span>
    );
  }
  return (
    <span className="relative z-10 inline-flex h-3.5 w-3.5 rounded-full border-2 bg-background" style={{ borderColor: 'hsl(var(--border))' }} aria-hidden />
  );
}

function Choice({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{hint}</p>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Fact({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: PALETTE_FOREST }} aria-hidden />
      <span>{children}</span>
    </li>
  );
}
